import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import WhiteAudioIcon from "../../assets/icons/white-audio.svg";
import PauseIcon from "../../assets/icons/audio-pause.svg";
import PlayIcon from "../../assets/icons/audio-play.svg";
import EditIcon from "../../assets/icons/audio-edit.svg";
import DeleteIcon from "../../assets/icons/audio-delete.svg";
import DeleteAudioModal from "./modals/DeleteAudioModal/DeleteAudioModal";
import { AppDispatch } from "../../redux/store";
import { quickFileService } from "../../services/quickFile.service";
import { updateAudioMetadataAction } from "../../redux/quickFile/quickFileSlice";
import "./VoiceChatListRow.less";

export interface VoiceChatListRowProps {
  title: string;
  date: string;
  duration: number;
  file?: any;
  audioUrl?: string;
  id?: string;
  albumId?: string;
  onClick?: () => void;
  select?: boolean;
  onSelectAndSend?: (file: any) => void;
  onDelete?: () => void;
}

const VoiceChatListRow = ({
  title,
  date,
  duration,
  onClick,
  file,
  audioUrl,
  id,
  albumId,
  select = false,
  onSelectAndSend,
  onDelete,
}: VoiceChatListRowProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [titleValue, setTitleValue] = useState(title);
  const [isSaving, setIsSaving] = useState(false);
  const originalTitle = useRef(title);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const src = audioUrl || file?.url || "";
  const hasSrc = Boolean(src);
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      if (el.duration > 0) setProgress(el.currentTime / el.duration);
    };
    const onEnded = () => setIsPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnded);
    };
  }, []);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const root = containerRef.current;
      if (!root) return;
      if (!root.contains(e.target as Node)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      const focusInput = () => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      };

      focusInput();

      const timeoutId = setTimeout(focusInput, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isEditing]);

  const togglePlay = async () => {
    if (!audioRef.current || !hasSrc) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    try {
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (e) {
      console.error("Error playing audio:", e);
    }
  };

  const handleRowClick = () => {
    onClick?.();
    setIsExpanded(true);
    // if (hasSrc) togglePlay();
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current || !audioRef.current.duration) return;
    const p = Number(e.target.value);
    audioRef.current.currentTime = p * audioRef.current.duration;
    setProgress(p);
  };

  const handleTitleUpdate = async () => {
    if (titleValue.trim() === "") {
      setTitleValue(originalTitle.current);
      setIsEditing(false);
      return;
    }

    if (titleValue !== originalTitle.current) {
      setIsSaving(true);
      try {
        // Determine the audioId - prioritize id prop, then fall back to file properties
        const audioId = id || file?.fileId || file?.id;

        if (albumId && audioId) {
          await dispatch(updateAudioMetadataAction({ albumId, audioId, title: titleValue })).unwrap();
          originalTitle.current = titleValue;
          message.success(t("title-updated-successfully") || "Title updated successfully");
        } else {
          throw new Error("Missing albumId or audioId");
        }
      } catch (error) {
        console.error("Failed to update title:", error);
        message.error(t("failed-to-update-title") || "Failed to update title");
        setTitleValue(originalTitle.current);
      } finally {
        setIsSaving(false);
        setIsEditing(false);
      }
    } else {
      setIsEditing(false);
    }
  };
  const handleDeleteAudio = async () => {
    const audioId = id || file?.fileId || file?.id;

    setIsDeleting(true);
    try {
      const payload = {
        albumId,
        filesId: [audioId],
      };

      await quickFileService.deleteAlbumFiles(payload);
      message.success(t("audio-deleted-successfully"));
      setIsDeleteOpen(false);
      onDelete?.();
    } catch (error) {
      console.error("Failed to delete audio:", error);
      message.error(t("back-error-message"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div ref={containerRef} className={`vc-row ${isExpanded ? "expanded" : ""}`.trim()} onClick={handleRowClick} role="button" tabIndex={0}>
      <img src={WhiteAudioIcon} alt="audio" className="vc-icon" />
      <div className="vc-info">
        {isEditing ? (
          <input
            ref={inputRef}
            className="vc-title-input"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              } else if (e.key === "Escape") {
                setTitleValue(originalTitle.current);
                setIsEditing(false);
              }
            }}
            onFocus={(e) => {
              e.currentTarget.select();
            }}
            onBlur={handleTitleUpdate}
            aria-label="Edit title"
          />
        ) : (
          <div className="vc-title-container">
            <div className="vc-title">{titleValue}</div>
            {isSaving && <span className="saving-indicator">({t("saving") || "saving..."})</span>}
          </div>
        )}
        <div className="vc-meta">
          <span className="vc-date">{date}</span>
          <span className="vc-duration">{`${minutes}:${seconds.toString().padStart(2, "0")}`}</span>
        </div>
      </div>

      {isExpanded && (
        <div className="vc-expanded">
          <input
            className="vc-progress"
            type="range"
            min={0}
            max={1}
            step={0.001}
            value={progress}
            style={{
              background: `linear-gradient(to right, #6E9FFF 0%, #6E9FFF ${progress * 100}%, #6E9FFF30 ${progress * 100}%, #6E9FFF30 100%)`,
            }}
            onChange={handleSeek}
            onClick={(e) => e.stopPropagation()}
            aria-label="progress"
          />
          <div className="vc-controls">
            <button
              type="button"
              className="vc-icon-btn vc-edit"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
                setIsEditing(true);
                originalTitle.current = titleValue;
                requestAnimationFrame(() => {
                  if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select();
                  }
                });
              }}
              disabled={isSaving}
              aria-label="edit"
            >
              <img src={EditIcon} alt="edit" style={{ opacity: isSaving ? 0.5 : 1 }} />
            </button>
            <button
              type="button"
              className="vc-icon-btn vc-toggle"
              onClick={(e) => {
                e.stopPropagation();
                togglePlay();
              }}
              aria-label={isPlaying ? "pause" : "play"}
            >
              <img src={isPlaying ? PauseIcon : PlayIcon} alt={isPlaying ? "pause" : "play"} />
            </button>
            <button
              type="button"
              className="vc-icon-btn vc-delete"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
                setIsDeleteOpen(true);
              }}
              aria-label="delete"
            >
              <img src={DeleteIcon} alt="delete" />
            </button>
          </div>
          {select && (
            <div>
              <button
                className="select-button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onSelectAndSend && file) {
                    onSelectAndSend(file);
                  }
                }}
              >
                {t("select-and-send") || "Select and Send"}
              </button>
            </div>
          )}
        </div>
      )}
      <audio ref={audioRef} src={src} className="vc-audio-element" />
      <DeleteAudioModal isOpen={isDeleteOpen} onCancel={() => setIsDeleteOpen(false)} onConfirm={handleDeleteAudio} isLoading={isDeleting} />
    </div>
  );
};

export default VoiceChatListRow;
